import {
  adminBannerListSchema,
  adminBannerSchema,
  adminFaqListSchema,
  adminFaqSchema,
  adminHeroSlideListSchema,
  adminHeroSlideSchema,
  adminNoticiaListSchema,
  adminNoticiaSchema,
  adminOfertaListSchema,
  adminOfertaSchema,
  adminPartnerListSchema,
  adminPartnerSchema,
  bannerPreviewSchema,
  bannerStatusChangeSchema,
  contentTypeSchema,
  createBannerSchema,
  createFaqSchema,
  createHeroSlideSchema,
  createNoticiaSchema,
  createOfertaSchema,
  createPartnerSchema,
  faqPreviewSchema,
  faqStatusChangeSchema,
  heroSlidePreviewSchema,
  heroSlideStatusChangeSchema,
  noticiaPreviewSchema,
  noticiaStatusChangeSchema,
  ofertaPreviewSchema,
  ofertaStatusChangeSchema,
  partnerPreviewSchema,
  partnerStatusChangeSchema,
  updateBannerSchema,
  updateFaqSchema,
  updateHeroSlideSchema,
  updateNoticiaSchema,
  updateOfertaSchema,
  updatePartnerSchema,
  type AdminBanner,
  type AdminFaq,
  type AdminHeroSlide,
  type AdminNoticia,
  type AdminOferta,
  type AdminPartner,
  type BannerPreview,
  type BannerStatusChange,
  type ContentType,
  type CreateBanner,
  type CreateFaq,
  type CreateHeroSlide,
  type CreateNoticia,
  type CreateOferta,
  type CreatePartner,
  type FaqPreview,
  type FaqStatusChange,
  type HeroSlidePreview,
  type HeroSlideStatusChange,
  type NoticiaPreview,
  type NoticiaStatusChange,
  type OfertaPreview,
  type OfertaStatusChange,
  type PartnerPreview,
  type PartnerStatusChange,
  type UpdateBanner,
  type UpdateFaq,
  type UpdateHeroSlide,
  type UpdateNoticia,
  type UpdateOferta,
  type UpdatePartner,
} from '@apuestapr/contracts';
import type { ZodType } from 'zod';

export interface ContentItemByType {
  readonly ofertas: AdminOferta;
  readonly noticias: AdminNoticia;
  readonly faqs: AdminFaq;
  readonly banners: AdminBanner;
  readonly 'hero-slides': AdminHeroSlide;
  readonly partners: AdminPartner;
}

export interface ContentCreateByType {
  readonly ofertas: CreateOferta;
  readonly noticias: CreateNoticia;
  readonly faqs: CreateFaq;
  readonly banners: CreateBanner;
  readonly 'hero-slides': CreateHeroSlide;
  readonly partners: CreatePartner;
}

export interface ContentUpdateByType {
  readonly ofertas: UpdateOferta;
  readonly noticias: UpdateNoticia;
  readonly faqs: UpdateFaq;
  readonly banners: UpdateBanner;
  readonly 'hero-slides': UpdateHeroSlide;
  readonly partners: UpdatePartner;
}

export interface ContentStatusByType {
  readonly ofertas: OfertaStatusChange;
  readonly noticias: NoticiaStatusChange;
  readonly faqs: FaqStatusChange;
  readonly banners: BannerStatusChange;
  readonly 'hero-slides': HeroSlideStatusChange;
  readonly partners: PartnerStatusChange;
}

export interface ContentPreviewByType {
  readonly ofertas: OfertaPreview;
  readonly noticias: NoticiaPreview;
  readonly faqs: FaqPreview;
  readonly banners: BannerPreview;
  readonly 'hero-slides': HeroSlidePreview;
  readonly partners: PartnerPreview;
}

interface ContentFormFieldBase {
  readonly key: string;
  readonly label: string;
  readonly nullable?: boolean;
  readonly optionalOnCreate?: boolean;
  readonly description?: string;
}

export type ContentFormField = ContentFormFieldBase &
  (
    | { readonly control: 'text' | 'textarea' | 'number' | 'keywords' }
    | { readonly control: 'image' }
    | { readonly control: 'category' }
    | {
        readonly control: 'select';
        readonly options: readonly { readonly value: string; readonly label: string }[];
      }
  );

export interface DashboardContentDefinition<Type extends ContentType> {
  readonly type: Type;
  readonly label: string;
  readonly primaryLabel: 'Título' | 'Pregunta' | 'Nombre';
  readonly primaryField: keyof ContentItemByType[Type];
  /** Null means the API owns an automatic order and the browser must not invent one. */
  readonly orderField: keyof ContentItemByType[Type] | null;
  readonly fields: readonly ContentFormField[];
  readonly knownCategories: readonly string[];
  readonly itemSchema: ZodType<ContentItemByType[Type]>;
  readonly listSchema: ZodType<readonly ContentItemByType[Type][]>;
  readonly createSchema: ZodType<ContentCreateByType[Type]>;
  readonly updateSchema: ZodType<ContentUpdateByType[Type]>;
  readonly statusSchema: ZodType<ContentStatusByType[Type]>;
  readonly previewSchema: ZodType<ContentPreviewByType[Type]>;
}

const placementOptions = [
  { value: 'modal', label: 'Modal' },
  { value: 'strip', label: 'Franja' },
] as const;

/**
 * The one closed dashboard catalog: route metadata, contracts, and every editable field for all six
 * content types. Category lists are editorial suggestions only; contracts intentionally remain free
 * text and the form preserves values outside these lists.
 */
export const dashboardContentCatalog = {
  ofertas: {
    type: 'ofertas',
    label: 'Ofertas',
    primaryLabel: 'Título',
    primaryField: 'titulo',
    orderField: 'ordenVisualizacion',
    fields: [
      { key: 'titulo', label: 'Título', control: 'text' },
      { key: 'descripcion', label: 'Descripción', control: 'textarea' },
      { key: 'imagenUrl', label: 'Imagen', control: 'image' },
      { key: 'categoria', label: 'Categoría', control: 'category' },
      { key: 'ordenVisualizacion', label: 'Orden de visualización', control: 'number' },
    ],
    knownCategories: ['Deportes', 'Casino', 'Promociones', 'Bonos'],
    itemSchema: adminOfertaSchema,
    listSchema: adminOfertaListSchema,
    createSchema: createOfertaSchema,
    updateSchema: updateOfertaSchema,
    statusSchema: ofertaStatusChangeSchema,
    previewSchema: ofertaPreviewSchema,
  },
  noticias: {
    type: 'noticias',
    label: 'Noticias',
    primaryLabel: 'Título',
    primaryField: 'titulo',
    orderField: null,
    fields: [
      { key: 'titulo', label: 'Título', control: 'text' },
      {
        key: 'slug',
        label: 'Slug',
        control: 'text',
        optionalOnCreate: true,
        description: 'En una noticia nueva puede dejarse vacío para que el servidor lo genere.',
      },
      { key: 'contenido', label: 'Contenido', control: 'textarea' },
      { key: 'imagenUrl', label: 'Imagen', control: 'image', nullable: true },
      { key: 'categoria', label: 'Categoría', control: 'category' },
    ],
    knownCategories: ['Actualidad', 'Deportes', 'Casino', 'Promociones'],
    itemSchema: adminNoticiaSchema,
    listSchema: adminNoticiaListSchema,
    createSchema: createNoticiaSchema,
    updateSchema: updateNoticiaSchema,
    statusSchema: noticiaStatusChangeSchema,
    previewSchema: noticiaPreviewSchema,
  },
  faqs: {
    type: 'faqs',
    label: 'Preguntas frecuentes',
    primaryLabel: 'Pregunta',
    primaryField: 'question',
    orderField: null,
    fields: [
      { key: 'question', label: 'Pregunta', control: 'text' },
      { key: 'answer', label: 'Respuesta', control: 'textarea' },
      { key: 'category', label: 'Categoría', control: 'category' },
      {
        key: 'keywords',
        label: 'Palabras clave',
        control: 'keywords',
        optionalOnCreate: true,
        description: 'Una por línea. Se usan para búsqueda editorial.',
      },
    ],
    knownCategories: ['Cuenta', 'Depósitos', 'Retiros', 'Apuestas', 'Bonos'],
    itemSchema: adminFaqSchema,
    listSchema: adminFaqListSchema,
    createSchema: createFaqSchema,
    updateSchema: updateFaqSchema,
    statusSchema: faqStatusChangeSchema,
    previewSchema: faqPreviewSchema,
  },
  banners: {
    type: 'banners',
    label: 'Banners',
    primaryLabel: 'Título',
    primaryField: 'titulo',
    orderField: 'orden',
    fields: [
      { key: 'slug', label: 'Slug', control: 'text' },
      { key: 'titulo', label: 'Título', control: 'text' },
      { key: 'mensaje', label: 'Mensaje', control: 'textarea' },
      { key: 'ctaLabel', label: 'Texto del llamado a la acción', control: 'text', nullable: true },
      { key: 'ctaUrl', label: 'URL del llamado a la acción', control: 'text', nullable: true },
      { key: 'placement', label: 'Ubicación', control: 'select', options: placementOptions },
      { key: 'orden', label: 'Orden', control: 'number' },
    ],
    knownCategories: [],
    itemSchema: adminBannerSchema,
    listSchema: adminBannerListSchema,
    createSchema: createBannerSchema,
    updateSchema: updateBannerSchema,
    statusSchema: bannerStatusChangeSchema,
    previewSchema: bannerPreviewSchema,
  },
  'hero-slides': {
    type: 'hero-slides',
    label: 'Hero',
    primaryLabel: 'Título',
    primaryField: 'titulo',
    orderField: 'orden',
    fields: [
      { key: 'slug', label: 'Slug', control: 'text' },
      { key: 'titulo', label: 'Título', control: 'text' },
      { key: 'descripcion', label: 'Descripción', control: 'textarea', nullable: true },
      { key: 'imagenUrl', label: 'Imagen', control: 'image' },
      {
        key: 'imagenAlt',
        label: 'Texto alternativo de la imagen',
        control: 'text',
        nullable: true,
      },
      { key: 'ctaLabel', label: 'Texto del llamado a la acción', control: 'text', nullable: true },
      { key: 'ctaUrl', label: 'URL del llamado a la acción', control: 'text', nullable: true },
      { key: 'orden', label: 'Orden', control: 'number' },
    ],
    knownCategories: [],
    itemSchema: adminHeroSlideSchema,
    listSchema: adminHeroSlideListSchema,
    createSchema: createHeroSlideSchema,
    updateSchema: updateHeroSlideSchema,
    statusSchema: heroSlideStatusChangeSchema,
    previewSchema: heroSlidePreviewSchema,
  },
  partners: {
    type: 'partners',
    label: 'Socios',
    primaryLabel: 'Nombre',
    primaryField: 'nombre',
    orderField: 'orden',
    fields: [
      { key: 'slug', label: 'Slug', control: 'text' },
      { key: 'nombre', label: 'Nombre', control: 'text' },
      { key: 'logoUrl', label: 'Logo', control: 'image', nullable: true },
      { key: 'sitioUrl', label: 'URL del sitio', control: 'text', nullable: true },
      { key: 'orden', label: 'Orden', control: 'number' },
    ],
    knownCategories: [],
    itemSchema: adminPartnerSchema,
    listSchema: adminPartnerListSchema,
    createSchema: createPartnerSchema,
    updateSchema: updatePartnerSchema,
    statusSchema: partnerStatusChangeSchema,
    previewSchema: partnerPreviewSchema,
  },
} as const satisfies {
  readonly [Type in ContentType]: DashboardContentDefinition<Type>;
};

export const parseDashboardContentType = (value: unknown): ContentType | null => {
  const parsed = contentTypeSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
};

export const getDashboardContentDefinition = <Type extends ContentType>(
  type: Type,
): DashboardContentDefinition<Type> =>
  dashboardContentCatalog[type] as unknown as DashboardContentDefinition<Type>;
